import { Controller } from "@hotwired/stimulus";

// Connects to data-controller="timezone"
export default class extends Controller {
  static targets = ["select"];

  connect() {
    this.detectAndSetTimezone();
  }

  detectAndSetTimezone() {
    try {
      // Get the browser's detected timezone
      const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Check if this is a select element or hidden field
      const isSelectElement =
        this.selectTarget.tagName.toLowerCase() === "select";

      // Only set if the field doesn't already have a value (for edit forms with existing data)
      if (this.selectTarget.value === "" || this.selectTarget.value === "UTC") {
        if (isSelectElement) {
          // Handle select dropdown
          const options = this.selectTarget.options;

          const timezone = this.convertToRailsTimezone(detectedTimezone);

          for (let i = 0; i < options.length; i++) {
            const option = options[i];
            // Check if the option value matches our detected timezone or contains it
            if (
              option.value === timezone ||
              option.text
                .toLowerCase()
                .includes(timezone.toLowerCase().replace("_", " "))
            ) {
              this.selectTarget.selectedIndex = i;
              break;
            }
          }

          // If no exact match found, try to find a reasonable fallback
          if (
            this.selectTarget.value === "" ||
            this.selectTarget.value === "UTC"
          ) {
            this.findTimezoneByOffset();
          }
        } else {
          // Handle hidden field - convert browser timezone to Rails timezone name
          const railsTimezone = this.convertToRailsTimezone(detectedTimezone);
          this.selectTarget.value = railsTimezone;
        }
      }
    } catch (error) {
      console.warn("Timezone detection failed:", error);
      // Fallback to UTC if detection fails
    }
  }

  findTimezoneByOffset() {
    try {
      // Get the current timezone offset in minutes
      const offsetMinutes = new Date().getTimezoneOffset();
      const offsetHours = -offsetMinutes / 60; // Negative because getTimezoneOffset returns opposite sign

      // Common timezone mappings for fallback
      const timezoneByOffset = {
        "-12": "International Date Line West",
        "-11": "American Samoa",
        "-10": "Hawaii",
        "-9": "Alaska",
        "-8": "Pacific Time (US & Canada)",
        "-7": "Mountain Time (US & Canada)",
        "-6": "Central Time (US & Canada)",
        "-5": "Eastern Time (US & Canada)",
        "-4": "Atlantic Time (Canada)",
        "-3": "Brasilia",
        "-2": "Mid-Atlantic",
        "-1": "Azores",
        0: "UTC",
        1: "Amsterdam",
        2: "Athens",
        3: "Moscow",
        4: "Abu Dhabi",
        5: "Islamabad",
        6: "Dhaka",
        7: "Bangkok",
        8: "Beijing",
        9: "Tokyo",
        10: "Sydney",
        11: "Magadan",
        12: "Auckland",
      };

      const fallbackTimezone = timezoneByOffset[offsetHours.toString()];
      if (fallbackTimezone) {
        const options = this.selectTarget.options;
        for (let i = 0; i < options.length; i++) {
          if (options[i].text.includes(fallbackTimezone)) {
            this.selectTarget.selectedIndex = i;
            break;
          }
        }
      }
    } catch (error) {
      console.warn("Timezone offset fallback failed:", error);
    }
  }

  convertToRailsTimezone(browserTimezone) {
    // Map browser timezone names to Rails ActiveSupport::TimeZone names
    const browserToRailsMapping = {
      "America/New_York": "Eastern Time (US & Canada)",
      "America/Chicago": "Central Time (US & Canada)",
      "America/Denver": "Mountain Time (US & Canada)",
      "America/Los_Angeles": "Pacific Time (US & Canada)",
      "America/Anchorage": "Alaska",
      "Pacific/Honolulu": "Hawaii",
      "America/Phoenix": "Arizona",
      "America/Toronto": "Eastern Time (US & Canada)",
      "America/Vancouver": "Pacific Time (US & Canada)",
      "America/Mexico_City": "Mexico City",
      "America/Sao_Paulo": "Brasilia",
      "America/Buenos_Aires": "Buenos Aires",
      "America/Caracas": "Caracas",
      "America/Halifax": "Atlantic Time (Canada)",
      "America/St_Johns": "Newfoundland",
      "Europe/London": "London",
      "Europe/Paris": "Paris",
      "Europe/Berlin": "Berlin",
      "Europe/Rome": "Rome",
      "Europe/Madrid": "Madrid",
      "Europe/Amsterdam": "Amsterdam",
      "Europe/Brussels": "Brussels",
      "Europe/Vienna": "Vienna",
      "Europe/Zurich": "Zurich",
      "Europe/Prague": "Prague",
      "Europe/Warsaw": "Warsaw",
      "Europe/Stockholm": "Stockholm",
      "Europe/Copenhagen": "Copenhagen",
      "Europe/Helsinki": "Helsinki",
      "Europe/Oslo": "Copenhagen", // Same timezone as Copenhagen
      "Europe/Moscow": "Moscow",
      "Europe/Athens": "Athens",
      "Europe/Istanbul": "Istanbul",
      "Asia/Tokyo": "Tokyo",
      "Asia/Shanghai": "Beijing",
      "Asia/Hong_Kong": "Hong Kong",
      "Asia/Singapore": "Singapore",
      "Asia/Seoul": "Seoul",
      "Asia/Taipei": "Taipei",
      "Asia/Bangkok": "Bangkok",
      "Asia/Jakarta": "Jakarta",
      "Asia/Manila": "Singapore", // Same timezone as Singapore
      "Asia/Kuala_Lumpur": "Kuala Lumpur",
      "Asia/Kolkata": "New Delhi",
      "Asia/Mumbai": "Mumbai",
      "Asia/Dubai": "Abu Dhabi",
      "Asia/Tehran": "Tehran",
      "Asia/Jerusalem": "Jerusalem",
      "Australia/Sydney": "Sydney",
      "Australia/Melbourne": "Melbourne",
      "Australia/Brisbane": "Brisbane",
      "Australia/Perth": "Perth",
      "Australia/Adelaide": "Adelaide",
      "Pacific/Auckland": "Auckland",
      "Pacific/Fiji": "Fiji",
      "Africa/Cairo": "Cairo",
      "Africa/Johannesburg": "Pretoria",
      "Africa/Lagos": "West Central Africa",
      "Africa/Nairobi": "Nairobi",
    };

    // Return mapped timezone or fallback to UTC
    return browserToRailsMapping[browserTimezone] || "UTC";
  }
}
